<template>
  <div class="combo-multi-picker">
    <div class="picker-toolbar">
      <el-input
        v-model="searchKey"
        size="small"
        placeholder="请输入方案名称搜索"
        clearable
        class="search-input"
        @keyup.enter.native="onSearch"
        @clear="onSearch"
      />
      <el-button
        type="primary"
        size="small"
        icon="el-icon-search"
        @click="onSearch"
      >搜索</el-button>
      <el-button size="small" @click="selectAllOnPage">全选当前页</el-button>
      <el-button size="small" @click="clearAll">清空已选</el-button>
      <span class="selected-tip">已选 {{ comboIds.length }} 项</span>
    </div>

    <el-table
      ref="table"
      :data="table.data"
      border
      stripe
      size="small"
      @selection-change="onSelectionChange"
    >
      <el-table-column type="selection" width="55" />
      <el-table-column prop="id" label="方案ID" width="100" />
      <el-table-column
        prop="comboName"
        label="方案名称"
        min-width="180"
        show-overflow-tooltip
      />
      <el-table-column prop="billNum" label="总期数" width="90">
        <template slot-scope="scope">
          <span>{{ scope.row.billNum }}期</span>
        </template>
      </el-table-column>
      <el-table-column prop="priceRate" label="费率" width="100">
        <template slot-scope="scope">
          <span>{{ scope.row.priceRate }}%</span>
        </template>
      </el-table-column>
      <el-table-column prop="prepayRate" label="首付比例" width="100">
        <template slot-scope="scope">
          <span>{{ scope.row.prepayRate }}%</span>
        </template>
      </el-table-column>
    </el-table>

    <pagination
      :total="table.total"
      :page.sync="table.currentPage"
      :limit.sync="table.pageSize"
      @pagination="getList"
    />
  </div>
</template>

<script>
// 方案多选组件
// - 通过 fetchList prop 注入数据源（避免组件与具体接口耦合）
// - 跨页已选项由内部 selectedMap 维护，切换分页后通过 toggleRowSelection 回显
// - 顶部仅"全选当前页"与"清空已选"，不提供跨页一键全选（按计划文档 §5.4 / §8 硬性约束）
export default {
  name: "ComboMultiPicker",
  props: {
    // v-model：选中的方案 id 列表
    value: {
      type: Array,
      default: () => [],
    },
    // 单页条数（默认 10）
    pageSize: {
      type: Number,
      default: 10,
    },
    // 父组件注入：参数 { page, pageSize, comboName }，返回 Promise<{ list, total }>
    fetchList: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      searchKey: "",
      table: {
        currentPage: 1,
        pageSize: this.pageSize,
        total: 0,
        data: [],
      },
      // 跨页已选 id 集合（用对象便于按 id 维护）
      selectedMap: {},
      // 正在恢复选中状态标志（防止 selection-change 清空 selectedMap）
      isRestoring: false,
    };
  },
  computed: {
    comboIds: {
      get() {
        return this.value || [];
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    // 外部 v-model 变化时（如父组件回填）同步到内部 selectedMap
    value: {
      immediate: true,
      handler(val) {
        const next = {};
        (val || []).forEach((id) => {
          next[String(id)] = true;  // 统一用字符串作为 key
        });
        this.selectedMap = next;
        this.isRestoring = true;
        this.$nextTick(() => {
          this.restoreSelection();
          this.isRestoring = false;
        });
      },
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    async getList() {
      this.isRestoring = true;  // 开始加载，防止 selection-change 清空 selectedMap
      const res = await this.fetchList({
        page: this.table.currentPage,
        pageSize: this.table.pageSize,
        comboName: this.searchKey || undefined,
      });
      this.table.data = res.list || [];
      this.table.total = res.total || 0;
      this.$nextTick(() => {
        this.restoreSelection();
        this.isRestoring = false;
      });
    },
    onSearch() {
      this.table.currentPage = 1;
      this.getList();
    },
    onSelectionChange(selection) {
      // 正在恢复选中时，不处理 selection-change 事件
      if (this.isRestoring) return;
      
      // 仅以"当前页"的 id 集合为基准更新 selectedMap
      const currentPageIds = this.table.data.map((r) => String(r.id));
      const newSelectionIds = new Set(selection.map((r) => String(r.id)));
      currentPageIds.forEach((id) => {
        if (newSelectionIds.has(id)) {
          this.selectedMap[id] = true;
        } else {
          delete this.selectedMap[id];
        }
      });
      this.emitValue();
    },
    selectAllOnPage() {
      // 仅选中当前页所有行
      this.table.data.forEach((r) => {
        this.selectedMap[r.id] = true;
      });
      this.$nextTick(() => {
        if (this.$refs.table && this.$refs.table.clearSelection) {
          this.$refs.table.clearSelection();
        }
        this.table.data.forEach((r) => {
          if (this.$refs.table) {
            this.$refs.table.toggleRowSelection(r, true);
          }
        });
        this.emitValue();
      });
    },
    clearAll() {
      this.selectedMap = {};
      if (this.$refs.table && this.$refs.table.clearSelection) {
        this.$refs.table.clearSelection();
      }
      this.emitValue();
    },
    emitValue() {
      const ids = Object.keys(this.selectedMap).map((k) => Number(k));  // 输出时转回数字
      this.comboIds = ids;
    },
    restoreSelection() {
      if (!this.$refs.table) return;
      this.table.data.forEach((r) => {
        if (this.selectedMap[String(r.id)]) {  // 统一用字符串匹配
          this.$refs.table.toggleRowSelection(r, true);
        }
      });
    },
    // 重置组件到初始状态（供父组件调用）
    reset() {
      this.searchKey = "";
      this.table.currentPage = 1;
      this.selectedMap = {};
      this.isRestoring = false;
      if (this.$refs.table && this.$refs.table.clearSelection) {
        this.$refs.table.clearSelection();
      }
      this.getList();
    },
  },
};
</script>

<style lang="scss" scoped>
.combo-multi-picker {
  .picker-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;

    .search-input {
      width: 220px;
    }

    .selected-tip {
      margin-left: auto;
      color: #909399;
      font-size: 13px;
    }
  }
}
</style>
